// Presentation-free: returns a decision, never toasts or renders — hosts own the messaging.
import { EErrorCode } from 'config/errors-codes';
import { sendApiRequest } from 'utils/network';

export type TDealerAccessStatus = 'allowed' | 'denied' | 'failed';

export interface IDealerAccessResult {
  status: TDealerAccessStatus;
  code?: EErrorCode;
}

interface IDealerAccessResponse {
  allowed: boolean;
  code?: EErrorCode;
}

/**
 * Requests the dealer whitelist decision from the backend and maps it to a status the caller can
 * act on. Never throws: a transport failure resolves to `{ status: 'failed' }` rather than
 * rejecting, so callers do not need their own try/catch around this function.
 */
export async function checkDealerAccess(params: {
  brand: string;
  market: string;
  dealerId: string;
}): Promise<IDealerAccessResult> {
  const { brand, market, dealerId } = params;
  const url = `/dealer-access?brand=${brand}&market=${market}`;
  const headers = {
    'X-Dealer': dealerId,
  };

  let response: IDealerAccessResponse | undefined;
  try {
    response = await sendApiRequest<IDealerAccessResponse>(url, {
      headers,
      suppressToast: true,
    });
  } catch {
    return { status: 'failed', code: EErrorCode.DEALER_ACCESS_CHECK_FAILED };
  }

  const result = response ?? { allowed: true };

  if (!result.allowed) {
    return { status: 'denied', code: result.code ?? EErrorCode.DEALER_NOT_WHITELISTED };
  }

  return { status: 'allowed' };
}
